import z from 'zod';
import { geographicZoneValue } from './geographic-zone';
import { timeUnitValue } from './time-unit';
/** Snake_case id used for procedures, steps, params. */
const calendarIdValue = z
    .string()
    .trim()
    .min(1)
    .regex(/^[a-z][\d_a-z]*$/, 'Must be snake_case starting with a letter');
const nonEmptyStringValue = z.string().trim().min(1);
const conditionExpressionValue = nonEmptyStringValue;
const formulaExpressionValue = nonEmptyStringValue;
export const CALENDAR_STEP_KIND_CALCULATED = 'calculated';
export const CALENDAR_STEP_KIND_INPUT = 'input';
export const CALENDAR_STEP_KIND_INPUT_OR_CALCULATED = 'input_or_calculated';
export const calendarStepKindValue = z.enum([
    CALENDAR_STEP_KIND_INPUT,
    CALENDAR_STEP_KIND_CALCULATED,
    CALENDAR_STEP_KIND_INPUT_OR_CALCULATED,
]);
export const CALENDAR_SUB_STEP_KIND_DATE_INPUT = 'date_input';
export const calendarSubStepKindValue = z.enum([
    CALENDAR_SUB_STEP_KIND_DATE_INPUT,
]);
export const CALENDAR_PARAMETER_KIND_ENUM = 'enum';
export const CALENDAR_PARAMETER_KIND_INTEGER = 'integer';
export const calendarParameterKindValue = z.enum([
    CALENDAR_PARAMETER_KIND_ENUM,
    CALENDAR_PARAMETER_KIND_INTEGER,
]);
export const CALENDAR_RULE_OUTCOME_CALCULATED = 'calculated';
export const CALENDAR_RULE_OUTCOME_INPUT = 'input';
export const calendarRuleOutcomeValue = z.enum([
    CALENDAR_RULE_OUTCOME_INPUT,
    CALENDAR_RULE_OUTCOME_CALCULATED,
]);
export const CALENDAR_PARAMETER_IMPACT_HOLIDAY_TABLE = 'holiday_table';
export const calendarParameterImpactValue = z.enum([
    CALENDAR_PARAMETER_IMPACT_HOLIDAY_TABLE,
]);
const literalFormulaParameterValue = z.object({
    description: nonEmptyStringValue.optional(),
    unit: timeUnitValue.optional(),
    value: z.number(),
});
const sourcedFormulaParameterValue = z.object({
    description: nonEmptyStringValue.optional(),
    source: nonEmptyStringValue,
    unit: timeUnitValue.optional(),
});
export const formulaParameterValue = z.union([
    literalFormulaParameterValue,
    sourcedFormulaParameterValue,
]);
export const weekendAdjustmentValue = z.object({
    description: nonEmptyStringValue.optional(),
    rule: formulaExpressionValue,
});
export const RESULT_ADJUSTMENT_KIND_WEEKDAY_CALENDAR_DAY_SHIFTS = 'weekday_calendar_day_shifts';
export const RESULT_ADJUSTMENT_KIND_NEXT_WORKING_DAY = 'next_working_day';
export const RESULT_ADJUSTMENT_KIND_PREVIOUS_WORKING_DAY = 'previous_working_day';
export const RESULT_ADJUSTMENT_KIND_WEEKDAY_ALTERNATE_WORKDAY_COUNTS = 'weekday_alternate_workday_counts';
export const resultAdjustmentKindValue = z.enum([
    RESULT_ADJUSTMENT_KIND_WEEKDAY_CALENDAR_DAY_SHIFTS,
    RESULT_ADJUSTMENT_KIND_NEXT_WORKING_DAY,
    RESULT_ADJUSTMENT_KIND_PREVIOUS_WORKING_DAY,
    RESULT_ADJUSTMENT_KIND_WEEKDAY_ALTERNATE_WORKDAY_COUNTS,
]);
const isoWeekdayValue = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
]);
export const weekdayCalendarDayShiftValue = z.object({
    calendarDays: z.number().int(),
    weekday: isoWeekdayValue,
});
export const weekdayCalendarDayShiftsAdjustmentValue = z.object({
    description: nonEmptyStringValue.optional(),
    kind: z.literal(RESULT_ADJUSTMENT_KIND_WEEKDAY_CALENDAR_DAY_SHIFTS),
    shifts: z.array(weekdayCalendarDayShiftValue).min(1),
});
export const nextWorkingDayAdjustmentValue = z.object({
    description: nonEmptyStringValue.optional(),
    kind: z.literal(RESULT_ADJUSTMENT_KIND_NEXT_WORKING_DAY),
});
export const previousWorkingDayAdjustmentValue = z.object({
    description: nonEmptyStringValue.optional(),
    kind: z.literal(RESULT_ADJUSTMENT_KIND_PREVIOUS_WORKING_DAY),
});
export const weekdayAlternateWorkdayCountValue = z.object({
    weekday: isoWeekdayValue,
    workingDays: z.number().int(),
});
export const weekdayAlternateWorkdayCountsAdjustmentValue = z.object({
    baseDateId: calendarIdValue,
    baseWorkingDays: z.number().int(),
    counts: z.array(weekdayAlternateWorkdayCountValue).min(1),
    description: nonEmptyStringValue.optional(),
    /**
     * When probe WORKDAY(base, baseWorkingDays) weekday
     * matches no count entry → WORKDAY(base, fallback)
     * instead of keeping the probe date. XLSM quirk when
     * probe n ≠ default n (ex. cadre économique D27).
     */
    fallbackWorkingDays: z.number().int().optional(),
    kind: z.literal(RESULT_ADJUSTMENT_KIND_WEEKDAY_ALTERNATE_WORKDAY_COUNTS),
});
export const resultAdjustmentValue = z.discriminatedUnion('kind', [
    weekdayCalendarDayShiftsAdjustmentValue,
    nextWorkingDayAdjustmentValue,
    previousWorkingDayAdjustmentValue,
    weekdayAlternateWorkdayCountsAdjustmentValue,
]).superRefine((adjustment, context) => {
    if (adjustment.kind
        === RESULT_ADJUSTMENT_KIND_WEEKDAY_CALENDAR_DAY_SHIFTS) {
        const seenWeekdays = new Set();
        for (const [index, shift] of adjustment.shifts.entries()) {
            if (seenWeekdays.has(shift.weekday)) {
                context.addIssue({
                    code: 'custom',
                    message: `Duplicate weekday ${String(shift.weekday)}`,
                    path: ['shifts', index, 'weekday'],
                });
            }
            seenWeekdays.add(shift.weekday);
        }
        return;
    }
    if (adjustment.kind
        !== RESULT_ADJUSTMENT_KIND_WEEKDAY_ALTERNATE_WORKDAY_COUNTS) {
        return;
    }
    const seenWeekdays = new Set();
    for (const [index, count] of adjustment.counts.entries()) {
        if (seenWeekdays.has(count.weekday)) {
            context.addIssue({
                code: 'custom',
                message: `Duplicate weekday ${String(count.weekday)}`,
                path: ['counts', index, 'weekday'],
            });
        }
        seenWeekdays.add(count.weekday);
    }
});
export const CALENDAR_CONSTRAINT_ON_VIOLATION_CLEAR = 'clear';
export const CALENDAR_CONSTRAINT_ON_VIOLATION_CLAMP = 'clamp';
export const calendarConstraintOnViolationValue = z.enum([
    CALENDAR_CONSTRAINT_ON_VIOLATION_CLEAR,
    CALENDAR_CONSTRAINT_ON_VIOLATION_CLAMP,
]);
export const calendarConstraintValue = z.object({
    description: nonEmptyStringValue,
    formula: formulaExpressionValue.optional(),
    legalReference: nonEmptyStringValue.optional(),
    message: nonEmptyStringValue.optional(),
    onViolation: calendarConstraintOnViolationValue.optional(),
});
/**
 * Cross-step chronological guard (VBA MsgBox + ClearContents).
 * If laterStepId is before earlierStepId + minimumCalendarDays
 * (default 0 → strict chronological order) → clear listed
 * step ids from inputs and re-evaluate.
 */
export const calendarCoherenceRuleValue = z.object({
    activeWhen: conditionExpressionValue.optional(),
    clearStepIds: z.array(calendarIdValue).min(1),
    earlierStepId: calendarIdValue,
    id: calendarIdValue,
    laterStepId: calendarIdValue,
    message: nonEmptyStringValue,
    /**
     * Minimum calendar-day gap later − earlier.
     * 0 (default) = later must be ≥ earlier.
     * 5 = later must be ≥ earlier + 5 calendar days.
     */
    minimumCalendarDays: z.number().int().nonnegative().optional(),
}).superRefine((rule, context) => {
    if (rule.earlierStepId === rule.laterStepId) {
        context.addIssue({
            code: 'custom',
            message: 'earlierStepId and laterStepId must differ',
            path: ['laterStepId'],
        });
    }
});
/**
 * Non-blocking UX alert computed after evaluation
 * (e.g. salary resume one month after inaptitude avis).
 */
export const calendarUxAlertValue = z.object({
    anchorStepId: calendarIdValue,
    id: calendarIdValue,
    message: nonEmptyStringValue,
    offsetCalendarMonths: z.number().int().positive(),
});
export const calendarCalculationRuleValue = z.object({
    condition: conditionExpressionValue.optional(),
    dateInput: calendarIdValue.optional(),
    description: nonEmptyStringValue.optional(),
    formula: formulaExpressionValue.optional(),
    formulaDescription: nonEmptyStringValue.optional(),
    note: nonEmptyStringValue.optional(),
    outcome: calendarRuleOutcomeValue.optional(),
    parameters: z
        .record(z.string(), formulaParameterValue)
        .optional(),
    resultAdjustment: resultAdjustmentValue.optional(),
    totalWorkingDaysExpression: nonEmptyStringValue.optional(),
    weekendAdjustment: z
        .union([weekendAdjustmentValue, formulaExpressionValue])
        .optional(),
}).superRefine((rule, context) => {
    const outcome = rule.outcome ?? CALENDAR_RULE_OUTCOME_CALCULATED;
    if (outcome === CALENDAR_RULE_OUTCOME_CALCULATED
        && rule.formula === undefined) {
        context.addIssue({
            code: 'custom',
            message: 'Calculated rule requires formula',
            path: ['formula'],
        });
    }
    if (outcome === CALENDAR_RULE_OUTCOME_INPUT
        && rule.dateInput === undefined) {
        context.addIssue({
            code: 'custom',
            message: 'Input rule requires dateInput',
            path: ['dateInput'],
        });
    }
});
export const calendarSubStepValue = z.object({
    activeWhen: conditionExpressionValue.optional(),
    id: calendarIdValue,
    kind: calendarSubStepKindValue,
    label: nonEmptyStringValue,
});
export const calendarStepValue = z.object({
    activeWhen: conditionExpressionValue.optional(),
    allowOverride: z.boolean().optional(),
    description: nonEmptyStringValue.optional(),
    formula: formulaExpressionValue.optional(),
    formulaDescription: nonEmptyStringValue.optional(),
    id: calendarIdValue,
    kind: calendarStepKindValue,
    label: nonEmptyStringValue,
    legalReference: nonEmptyStringValue.optional(),
    maxConstraint: calendarConstraintValue.optional(),
    minConstraint: calendarConstraintValue.optional(),
    note: nonEmptyStringValue.optional(),
    parameters: z
        .record(z.string(), formulaParameterValue)
        .optional(),
    resultAdjustment: resultAdjustmentValue.optional(),
    rules: z.array(calendarCalculationRuleValue).optional(),
    stepNumber: z.number().int().positive().optional(),
    subSteps: z.array(calendarSubStepValue).optional(),
    /**
     * Non-binding prefill hint for an `input` step (e.g. an
     * indicative delay before a legally-free date). Evaluated
     * like `formula` but never validated, never blocks the
     * step, and carries no lock: the front-end may display it
     * as a starting value, the user may always override it in
     * either direction. Unlike `allowOverride`, there is no
     * "below minimum" rejection.
     */
    suggestedFormula: formulaExpressionValue.optional(),
    suggestedResultAdjustment: resultAdjustmentValue.optional(),
    weekendAdjustment: weekendAdjustmentValue.optional(),
}).superRefine((step, context) => {
    if (step.suggestedFormula !== undefined
        && step.kind !== CALENDAR_STEP_KIND_INPUT
        && step.kind !== CALENDAR_STEP_KIND_INPUT_OR_CALCULATED) {
        context.addIssue({
            code: 'custom',
            message: 'suggestedFormula is only allowed on input or '
                + 'input_or_calculated steps',
            path: ['suggestedFormula'],
        });
    }
    if (step.suggestedResultAdjustment !== undefined
        && step.suggestedFormula === undefined) {
        context.addIssue({
            code: 'custom',
            message: 'suggestedResultAdjustment requires suggestedFormula',
            path: ['suggestedResultAdjustment'],
        });
    }
    if (step.kind === CALENDAR_STEP_KIND_INPUT) {
        return;
    }
    const hasRules = step.rules !== undefined && step.rules.length > 0;
    const hasFormula = step.formula !== undefined;
    if (!hasRules && !hasFormula) {
        context.addIssue({
            code: 'custom',
            message: 'Calculated step requires rules or formula',
            path: ['rules'],
        });
    }
});
export const calendarEnumParameterValue = z.object({
    activeWhen: conditionExpressionValue.optional(),
    defaultValue: z.string().optional(),
    description: nonEmptyStringValue.optional(),
    impact: calendarParameterImpactValue.optional(),
    kind: z.literal(CALENDAR_PARAMETER_KIND_ENUM),
    label: nonEmptyStringValue.optional(),
    valueLabels: z
        .record(z.string(), nonEmptyStringValue)
        .optional(),
    values: z.array(nonEmptyStringValue).min(1),
}).superRefine((parameter, context) => {
    if (parameter.defaultValue !== undefined
        && !parameter.values.includes(parameter.defaultValue)) {
        context.addIssue({
            code: 'custom',
            message: 'defaultValue must be one of values',
            path: ['defaultValue'],
        });
    }
    if (parameter.valueLabels === undefined) {
        return;
    }
    for (const valueKey of Object.keys(parameter.valueLabels)) {
        if (!parameter.values.includes(valueKey)) {
            context.addIssue({
                code: 'custom',
                message: `valueLabels key "${valueKey}" must be one of values`,
                path: ['valueLabels', valueKey],
            });
        }
    }
});
export const calendarIntegerParameterValue = z.object({
    activeWhen: conditionExpressionValue.optional(),
    defaultValue: z.number().int().optional(),
    description: nonEmptyStringValue.optional(),
    kind: z.literal(CALENDAR_PARAMETER_KIND_INTEGER),
    label: nonEmptyStringValue.optional(),
    maximum: z.number().int().optional(),
    minimum: z.number().int().optional(),
    note: nonEmptyStringValue.optional(),
    unit: timeUnitValue,
}).superRefine((parameter, context) => {
    if (parameter.minimum !== undefined
        && parameter.maximum !== undefined
        && parameter.minimum > parameter.maximum) {
        context.addIssue({
            code: 'custom',
            message: 'minimum must be <= maximum',
            path: ['minimum'],
        });
    }
    if (parameter.defaultValue === undefined) {
        return;
    }
    if (parameter.minimum !== undefined
        && parameter.defaultValue < parameter.minimum) {
        context.addIssue({
            code: 'custom',
            message: 'defaultValue must be >= minimum',
            path: ['defaultValue'],
        });
    }
    if (parameter.maximum !== undefined
        && parameter.defaultValue > parameter.maximum) {
        context.addIssue({
            code: 'custom',
            message: 'defaultValue must be <= maximum',
            path: ['defaultValue'],
        });
    }
});
export const calendarParameterValue = z.discriminatedUnion('kind', [calendarEnumParameterValue, calendarIntegerParameterValue]);
/**
 * Declares the three step roles a reverse search
 * (`findLatestValidInputDateForDefinition`) needs: which step is
 * the unknown to solve for, which step is the bound checked
 * against the desired outcome date, and which step is surfaced to
 * the user as the outcome. Declarative only — no search logic
 * lives in the JSON, this just names roles the engine already
 * knows how to read.
 */
export const calendarReverseSearchValue = z.object({
    boundStepId: calendarIdValue,
    description: nonEmptyStringValue.optional(),
    inputStepId: calendarIdValue,
    label: nonEmptyStringValue.optional(),
    outcomeStepId: calendarIdValue,
});
/**
 * Visual section heading transcribed from an `.xlsm` blue bar
 * (white text on dark blue fill). Pure display metadata —
 * never evaluated as a date step.
 *
 * `before` anchors where the UI inserts the heading:
 * - `parameters` → above the form (legacy block start)
 * - `steps` → above the first step in form order (legacy)
 * - a parameter id or step id → immediately before that field
 */
export const CALENDAR_INTERTITLE_BEFORE_PARAMETERS = 'parameters';
export const CALENDAR_INTERTITLE_BEFORE_STEPS = 'steps';
export const calendarIntertitleBeforeValue = z.union([
    z.literal(CALENDAR_INTERTITLE_BEFORE_PARAMETERS),
    z.literal(CALENDAR_INTERTITLE_BEFORE_STEPS),
    calendarIdValue,
]);
export const calendarIntertitleValue = z.object({
    before: calendarIntertitleBeforeValue,
    id: calendarIdValue,
    label: nonEmptyStringValue,
});
export const calendarHelperValue = z.object({
    description: nonEmptyStringValue,
    example: nonEmptyStringValue.optional(),
    exclusions: z.array(nonEmptyStringValue).optional(),
    note: nonEmptyStringValue.optional(),
    signature: nonEmptyStringValue,
    specificities: z
        .record(z.string(), z.array(nonEmptyStringValue))
        .optional(),
    zones: z.array(geographicZoneValue).optional(),
});
export const calendarRuleDefinitionValue = z.object({
    coherenceRules: z.array(calendarCoherenceRuleValue).optional(),
    /**
     * Excel / xlsm row order for the input form: parameter ids and
     * step ids interleaved. When omitted, the UI falls back to
     * parameters then steps (declaration order).
     */
    formFieldOrder: z.array(calendarIdValue).optional(),
    globalConstraint: calendarConstraintValue.optional(),
    helpers: z
        .record(z.string(), calendarHelperValue)
        .optional(),
    implementationNotes: z
        .array(nonEmptyStringValue)
        .optional(),
    intertitles: z.array(calendarIntertitleValue).optional(),
    legalReference: nonEmptyStringValue.optional(),
    parameters: z.record(z.string(), calendarParameterValue),
    procedureId: calendarIdValue,
    reverseSearch: calendarReverseSearchValue.optional(),
    steps: z.array(calendarStepValue).min(1),
    uxAlerts: z.array(calendarUxAlertValue).optional(),
}).superRefine((definition, context) => {
    const stepIds = new Set();
    for (const [index, step] of definition.steps.entries()) {
        if (stepIds.has(step.id)) {
            context.addIssue({
                code: 'custom',
                message: `Duplicate step id "${step.id}"`,
                path: ['steps', index, 'id'],
            });
        }
        stepIds.add(step.id);
        const subStepIds = new Set();
        for (const [subIndex, subStep,] of (step.subSteps ?? []).entries()) {
            if (subStepIds.has(subStep.id) || stepIds.has(subStep.id)) {
                context.addIssue({
                    code: 'custom',
                    message: `Duplicate sub-step id "${subStep.id}"`,
                    path: ['steps', index, 'subSteps', subIndex, 'id'],
                });
            }
            subStepIds.add(subStep.id);
        }
    }
    for (const [index, rule,] of (definition.coherenceRules ?? []).entries()) {
        if (!stepIds.has(rule.earlierStepId)) {
            context.addIssue({
                code: 'custom',
                message: `Unknown earlierStepId "${rule.earlierStepId}"`,
                path: ['coherenceRules', index, 'earlierStepId'],
            });
        }
        if (!stepIds.has(rule.laterStepId)) {
            context.addIssue({
                code: 'custom',
                message: `Unknown laterStepId "${rule.laterStepId}"`,
                path: ['coherenceRules', index, 'laterStepId'],
            });
        }
        for (const [clearIndex, clearStepId,] of rule.clearStepIds.entries()) {
            if (!stepIds.has(clearStepId)) {
                context.addIssue({
                    code: 'custom',
                    message: `Unknown clearStepId "${clearStepId}"`,
                    path: [
                        'coherenceRules',
                        index,
                        'clearStepIds',
                        clearIndex,
                    ],
                });
            }
        }
    }
    for (const [index, alert,] of (definition.uxAlerts ?? []).entries()) {
        if (!stepIds.has(alert.anchorStepId)) {
            context.addIssue({
                code: 'custom',
                message: `Unknown uxAlert anchorStepId "${alert.anchorStepId}"`,
                path: ['uxAlerts', index, 'anchorStepId'],
            });
        }
    }
    if (definition.reverseSearch !== undefined) {
        const { boundStepId, inputStepId, outcomeStepId } = definition.reverseSearch;
        if (!stepIds.has(boundStepId)) {
            context.addIssue({
                code: 'custom',
                message: `Unknown reverseSearch boundStepId "${boundStepId}"`,
                path: ['reverseSearch', 'boundStepId'],
            });
        }
        if (!stepIds.has(inputStepId)) {
            context.addIssue({
                code: 'custom',
                message: `Unknown reverseSearch inputStepId "${inputStepId}"`,
                path: ['reverseSearch', 'inputStepId'],
            });
        }
        if (!stepIds.has(outcomeStepId)) {
            context.addIssue({
                code: 'custom',
                message: `Unknown reverseSearch outcomeStepId "${outcomeStepId}"`,
                path: ['reverseSearch', 'outcomeStepId'],
            });
        }
    }
    const parameterIds = new Set(Object.keys(definition.parameters));
    if (definition.formFieldOrder !== undefined) {
        const seenFormFieldIds = new Set();
        for (const [index, fieldId,] of definition.formFieldOrder.entries()) {
            if (seenFormFieldIds.has(fieldId)) {
                context.addIssue({
                    code: 'custom',
                    message: `Duplicate formFieldOrder id "${fieldId}"`,
                    path: ['formFieldOrder', index],
                });
            }
            seenFormFieldIds.add(fieldId);
            if (!parameterIds.has(fieldId)
                && !stepIds.has(fieldId)) {
                context.addIssue({
                    code: 'custom',
                    message: `Unknown formFieldOrder id "${fieldId}"`,
                    path: ['formFieldOrder', index],
                });
            }
        }
        for (const parameterId of parameterIds) {
            if (!seenFormFieldIds.has(parameterId)) {
                context.addIssue({
                    code: 'custom',
                    message: `Parameter "${parameterId}" missing from formFieldOrder`,
                    path: ['formFieldOrder'],
                });
            }
        }
        for (const stepId of stepIds) {
            if (!seenFormFieldIds.has(stepId)) {
                context.addIssue({
                    code: 'custom',
                    message: `Step "${stepId}" missing from formFieldOrder`,
                    path: ['formFieldOrder'],
                });
            }
        }
    }
    const intertitleIds = new Set();
    const intertitleBefores = new Set();
    for (const [index, intertitle,] of (definition.intertitles ?? []).entries()) {
        if (intertitleIds.has(intertitle.id)) {
            context.addIssue({
                code: 'custom',
                message: `Duplicate intertitle id "${intertitle.id}"`,
                path: ['intertitles', index, 'id'],
            });
        }
        intertitleIds.add(intertitle.id);
        if (stepIds.has(intertitle.id)) {
            context.addIssue({
                code: 'custom',
                message: `Intertitle id "${intertitle.id}" collides with a step id`,
                path: ['intertitles', index, 'id'],
            });
        }
        if (parameterIds.has(intertitle.id)) {
            context.addIssue({
                code: 'custom',
                message: `Intertitle id "${intertitle.id}" collides with a parameter id`,
                path: ['intertitles', index, 'id'],
            });
        }
        if (intertitleBefores.has(intertitle.before)) {
            context.addIssue({
                code: 'custom',
                message: `Duplicate intertitle before "${intertitle.before}"`,
                path: ['intertitles', index, 'before'],
            });
        }
        intertitleBefores.add(intertitle.before);
        if (intertitle.before
            !== CALENDAR_INTERTITLE_BEFORE_PARAMETERS
            && intertitle.before
                !== CALENDAR_INTERTITLE_BEFORE_STEPS
            && !stepIds.has(intertitle.before)
            && !parameterIds.has(intertitle.before)) {
            context.addIssue({
                code: 'custom',
                message: `Unknown intertitle before "${intertitle.before}"`,
                path: ['intertitles', index, 'before'],
            });
        }
    }
});
export const parseCalendarRuleDefinition = (value) => calendarRuleDefinitionValue.parse(value);
export const isCalendarRuleDefinition = (value) => calendarRuleDefinitionValue.safeParse(value).success;
export function findCalendarIntertitle(definition, before) {
    return definition.intertitles?.find(intertitle => intertitle.before === before);
}
/**
 * Form display order matching the Excel / xlsm rows. Falls back
 * to parameters-then-steps when `formFieldOrder` is omitted.
 */
export function listCalendarFormFieldOrder(definition) {
    if (definition.formFieldOrder !== undefined) {
        return definition.formFieldOrder;
    }
    return [
        ...Object.keys(definition.parameters),
        ...definition.steps.map(step => step.id),
    ];
}
/**
 * Intertitle shown above an agenda event for `stepId`. Resolves
 * anchors on the step itself, on a parameter that sits between
 * the previous step and this one in form order, or the legacy
 * `before: "steps"` anchor on the first step.
 */
export function findCalendarAgendaIntertitleForStep(definition, stepId) {
    const onStep = findCalendarIntertitle(definition, stepId);
    if (onStep !== undefined) {
        return onStep;
    }
    const order = listCalendarFormFieldOrder(definition);
    const stepIndex = order.indexOf(stepId);
    if (stepIndex === -1) {
        return undefined;
    }
    let previousStepIndex = -1;
    for (let index = stepIndex - 1; index >= 0; index -= 1) {
        const fieldId = order[index];
        if (definition.steps.some(step => step.id === fieldId)) {
            previousStepIndex = index;
            break;
        }
    }
    for (let index = previousStepIndex + 1; index < stepIndex; index += 1) {
        const fieldId = order[index];
        if (!(fieldId in definition.parameters)) {
            continue;
        }
        const onParameter = findCalendarIntertitle(definition, fieldId);
        if (onParameter !== undefined) {
            return onParameter;
        }
    }
    const firstStepId = order.find(fieldId => definition.steps.some(step => step.id === fieldId));
    if (firstStepId === stepId) {
        return findCalendarIntertitle(definition, CALENDAR_INTERTITLE_BEFORE_STEPS);
    }
    return undefined;
}
//# sourceMappingURL=calendar-rule-definition.js.map